import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

/** Crawl record from the API. Mirrors the `crawls` D1 table — the server uses
 *  SELECT * so every column lands on the client. Keep this in sync with
 *  app/drizzle/* migrations on the table, or TypeScript's drift-catching
 *  doesn't catch drift (the bar's "#1 bug source"). */
export interface Crawl {
  id: string
  site_id: string
  url: string
  domain: string
  status: 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled'
  config: string
  pages_found: number
  pages_crawled: number
  issues_count: number
  started_at: string
  completed_at: string | null
  created_at: string
  created_by?: string | null
  is_wordpress?: number
  wordpress_version?: string | null
  wordpress_seo_plugin?: string | null
  avg_seo_score?: number | null
  alert_sent?: number
  ai_crawler_rules?: string | null
  has_llms_txt?: number
  chat_notified?: number
  has_ai_analysis?: number
  health_score?: number | null
}

/** Page record from the API (matches all columns from pages table) */
export interface CrawlPage {
  id: string
  crawl_id: string
  url: string
  status_code: number
  response_time_ms: number
  content_type: string | null
  redirect_url: string | null
  redirect_chain: string | null
  // #25b — JSON array of {from, code} objects; populated server-side via
  // page_redirect_origins reverse JOIN. Empty/null when no inbound redirects.
  redirect_inbound: string | null
  title: string | null
  title_length: number
  meta_description: string | null
  meta_description_length: number
  canonical_url: string | null
  canonical_status: string
  meta_robots: string | null
  meta_robots_noindex: number
  meta_robots_nofollow: number
  h1_count: number
  h1_texts: string | null
  h2_count: number
  h2_texts: string | null
  h3_count: number
  h3_texts: string | null
  h4_count: number
  h5_count: number
  h6_count: number
  heading_hierarchy_valid: number
  word_count: number
  internal_links_count: number
  external_links_count: number
  images_count: number
  images_missing_alt: number
  images_data: string | null
  has_schema: number
  schema_types: string | null
  schema_data: string | null
  has_og_tags: number
  og_title: string | null
  og_description: string | null
  og_image: string | null
  og_type: string | null
  twitter_card: string | null
  twitter_title: string | null
  twitter_description: string | null
  hreflang_tags: string | null
  has_viewport: number
  content_language: string | null
  mixed_content_count: number
  mixed_content_urls: string | null
  response_headers: string | null
  seo_score: number | null
  resources_data: string | null
  custom_extractions: string | null
  us_spellings: string | null
  depth: number | null
  readability_score: number | null
  meta_keywords: string | null
  images_missing_dimensions: number
  images_no_lazy_loading: number
  inrank: number | null
  pagination_next: string | null
  pagination_prev: string | null
  last_modified: string | null
  // Lighthouse / Core Web Vitals (DataForSEO)
  psi_performance: number | null
  psi_accessibility: number | null
  psi_best_practices: number | null
  psi_seo: number | null
  psi_lcp: number | null
  psi_cls: number | null
  psi_tbt: number | null
  psi_fcp: number | null
  psi_si: number | null
  screenshot_key: string | null
  screenshot_mobile_key: string | null
  hidden_link_count: number | null
  // Enhanced audit (Browser Rendering /markdown + /json)
  markdown_content: string | null
  ai_page_audit: string | null
  ai_analysis: string | null
  // Browser Rendering (Phase 6D)
  js_rendered: number
  pre_render_word_count: number | null
  is_indexable: number
  crawled_at: string
}

/** Issue summary from the API */
export interface CrawlIssue {
  type: string
  label: string
  count: number
  severity: 'error' | 'warning' | 'info'
}

/** Dashboard aggregated stats */
export interface DashboardStats {
  uniqueDomains: number
  totalCrawls: number
  totalPages: number
  totalIssues: number
  activeSchedules: number
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => apiClient.get<DashboardStats>('/api/crawls/dashboard/stats'),
  })
}

/** List all crawls. Returns `crawls` (capped at server LIMIT, default 50)
 *  plus `totalCount` (true row count for the filter — for "Showing 50 of N"
 *  display so users know more rows exist beyond the cap). */
export function useCrawls(limit?: number) {
  return useQuery({
    queryKey: ['crawls', { limit }],
    queryFn: () => apiClient.get<{ crawls: Crawl[]; totalCount: number }>('/api/crawls', {
      params: limit ? { limit } : {},
    }),
  })
}

/** Get a single crawl by ID */
export function useCrawl(id: string) {
  return useQuery({
    queryKey: ['crawls', id],
    queryFn: () => apiClient.get<{ crawl: Crawl }>(`/api/crawls/${id}`),
    refetchInterval: (query) => {
      // Poll every 3s while running
      const crawl = query.state.data?.crawl
      return crawl?.status === 'running' ? 3000 : false
    },
  })
}

/** Get crawl issues */
export function useCrawlIssues(id: string) {
  return useQuery({
    queryKey: ['crawls', id, 'issues'],
    queryFn: () => apiClient.get<{ issues: CrawlIssue[] }>(`/api/crawls/${id}/issues`),
  })
}

/** Get pages affected by a specific issue (SQL or AI-detected) */
export function useIssuePages(crawlId: string, issueType: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'issues', issueType],
    queryFn: () => apiClient.get<{ pages: CrawlPage[] | AiIssuePage[]; totalCount?: number; isAiIssue?: boolean }>(`/api/crawls/${crawlId}/issues/${issueType}`),
    enabled: !!issueType,
  })
}

/** Query params for the pages endpoint */
export interface PageQueryParams {
  search?: string
  filter?: string
  limit?: number
  offset?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

/** Get crawled pages with search/pagination/sorting/filtering */
export function useCrawlPages(id: string, params: PageQueryParams = {}) {
  const { search = '', filter = '', limit = 50, offset = 0, sortBy = 'url', sortDir = 'asc' } = params
  return useQuery({
    queryKey: ['crawls', id, 'pages', { search, filter, limit, offset, sortBy, sortDir }],
    queryFn: () =>
      apiClient.get<{ pages: CrawlPage[]; total: number }>(`/api/crawls/${id}/pages`, {
        params: { search, filter, limit, offset, sort_by: sortBy, sort_dir: sortDir },
      }),
  })
}

/** External link domain grouping */
export interface ExternalLinkDomain {
  domain: string
  link_count: number
  source_pages: number
  /** #32 — worst HTTP status across all links to this domain. Null if no link was status-checked. */
  worst_status: number | null
}

/** Get external links grouped by domain.
 *
 * Returns up to 200 domains (ordered by link_count desc) plus the true
 * distinct-domain count for the "showing 200 of N" trust signal.
 */
export function useExternalLinkDomains(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'links', 'domains'],
    queryFn: () => apiClient.get<{ domains: ExternalLinkDomain[]; totalCount: number }>(`/api/crawls/${crawlId}/links/domains`),
  })
}

/** External link detail for a specific domain */
export interface ExternalLinkDetail {
  source_url: string
  target_url: string
  anchor_text: string
  rel: string
  is_nofollow: number
  status_code: number | null
  is_broken: number
}

/** Get external links for a specific domain */
export function useExternalLinkDomainDetail(crawlId: string, domain: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'links', 'domains', domain],
    queryFn: () =>
      apiClient.get<{ links: ExternalLinkDetail[] }>(
        `/api/crawls/${crawlId}/links/domains/${encodeURIComponent(domain!)}`,
      ),
    enabled: !!domain,
  })
}

/** External link with status check result */
export interface ExternalLinkStatus {
  target_url: string
  status_code: number | null
  final_status_code: number | null
  redirect_url: string | null
  is_broken: number
  link_count: number
  source_pages: number
}

/** External link status summary */
export interface ExternalLinkStatusSummary {
  ok: number
  redirect: number
  broken: number
  unchecked: number
}

/** Get external link status results */
export function useExternalLinkStatus(crawlId: string, statusFilter?: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'external-links', 'status', statusFilter],
    queryFn: () =>
      apiClient.get<{ links: ExternalLinkStatus[]; summary: ExternalLinkStatusSummary }>(
        `/api/crawls/${crawlId}/external-links/status`,
        { params: statusFilter ? { status: statusFilter } : {} },
      ),
  })
}

/** Get spam scores for external link domains */
export function useExternalSpamScores(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'links', 'spam-scores'],
    queryFn: () =>
      apiClient.get<{ scores: Array<{ target: string; spam_score: number }> }>(
        `/api/crawls/${crawlId}/links/spam-scores`,
      ),
    staleTime: 60 * 60 * 1000,
  })
}

/** Crawl overview stats (conditional aggregation) */
export interface CrawlOverview {
  statusCodes: { '2xx': number; '3xx': number; '4xx': number; '5xx': number }
  responseTime: { fast: number; ok: number; slow: number; verySlow: number; critical: number; avg: number }
  contentLength: { empty: number; thin: number; short: number; medium: number; long: number }
  seoReadiness: { canonical: number; schema: number; ogTags: number; viewport: number; total200: number }
  links: { total: number; internal: number; external: number; broken: number }
  seoScore: { good: number; ok: number; poor: number; avg: number }
  readability: { easy: number; moderate: number; difficult: number; unscored: number; avg: number }
  depth: { level0: number; level1to2: number; level3to4: number; level5plus: number }
  security: { hsts: number; csp: number; xFrameOptions: number; total200: number }
  indexability: { indexable: number; total200: number }
  inrank: { high: number; medium: number; low: number; avg: number }
  totalPages: number
}

export function useCrawlOverview(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'overview'],
    queryFn: () => apiClient.get<CrawlOverview>(`/api/crawls/${crawlId}/overview`),
  })
}

/** Page detail with inlinks/outlinks */
export interface LinkRecord {
  source_url?: string
  target_url?: string
  anchor_text: string | null
  link_type?: string
  status_code?: number | null
}

export interface PageDetail {
  page: CrawlPage
  inlinks: LinkRecord[]
  outlinks: LinkRecord[]
}

export function usePageDetail(crawlId: string, pageId: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'pages', pageId],
    queryFn: () => apiClient.get<PageDetail>(`/api/crawls/${crawlId}/pages/${pageId}`),
    enabled: !!pageId,
  })
}

/** Start a new crawl */
export interface CreateCrawlData {
  mode?: 'spider' | 'list'
  url?: string
  urls?: string[]
  maxPages?: number
  rateLimit: number
  respectRobots?: boolean
  checkExternalLinks?: boolean
  enableJsRendering?: boolean
  enableScreenshots?: boolean
  enableAiAnalysis?: boolean
  extractionRules?: Array<{ label: string; selector: string }>
}

export function useCreateCrawl() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCrawlData) =>
      apiClient.post<{ id: string; status: string }>('/api/crawls', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls'] })
    },
  })
}

/** Cancel a crawl */
export function useCancelCrawl() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/crawls/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls'] })
    },
  })
}

export function usePauseCrawl() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<{ ok: boolean }>(`/api/crawls/${id}/pause`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls'] })
    },
  })
}

export function useResumeCrawl() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<{ ok: boolean }>(`/api/crawls/${id}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls'] })
    },
  })
}

/** Export types */
export type ExportType = 'pages' | 'links' | 'issues' | 'sitemap-xml' | 'images' | 'schema' | 'spelling' | 'headings'
export type ExportFormat = 'csv' | 'json'

interface ExportResponse {
  downloadUrl: string
  filename: string
  size: number
}

/** Export a crawl's data */
export function useExportCrawl() {
  return useMutation({
    mutationFn: async ({ id, type, format }: { id: string; type: ExportType; format: ExportFormat }) => {
      const result = await apiClient.post<ExportResponse>(`/api/crawls/${id}/export`, { type, format })
      // Open the download URL in a new window/tab to trigger browser download
      window.open(result.downloadUrl, '_blank')
      return result
    },
  })
}

/** Domain crawl history (for comparison picker) */
export interface CrawlHistoryItem {
  id: string
  url: string
  domain: string
  status: string
  pages_found: number
  pages_crawled: number
  issues_count: number
  started_at: string
  completed_at: string | null
  created_at: string
}

export function useCrawlHistory(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'history'],
    queryFn: () => apiClient.get<{ crawls: CrawlHistoryItem[] }>(`/api/crawls/${crawlId}/history`),
    enabled,
  })
}

/** Crawl comparison result */
export interface CrawlComparison {
  crawl1: { id: string; domain: string; completedAt: string | null; pagesCrawled: number; issuesCount: number }
  crawl2: { id: string; domain: string; completedAt: string | null; pagesCrawled: number; issuesCount: number }
  summary: {
    added: number
    removed: number
    changed: number
    byField: Record<string, number>
  }
  pages: {
    added: string[]
    removed: string[]
    changed: Array<{ url: string; changes: Array<{ field: string; before: unknown; after: unknown }> }>
  }
}

export function useCrawlComparison(crawlId: string, otherId: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'compare', otherId],
    queryFn: () => apiClient.get<CrawlComparison>(`/api/crawls/${crawlId}/compare/${otherId}`),
    enabled: !!otherId,
  })
}

/** Link stats summary */
export interface LinkStats {
  total: number
  internal: number
  external: number
  follow: number
  nofollow: number
  broken: number
  emptyAnchor: number
  internalNofollow: number
}

export function useLinkStats(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'links', 'stats'],
    queryFn: () => apiClient.get<LinkStats>(`/api/crawls/${crawlId}/links/stats`),
  })
}

/** Anchor text frequency item */
export interface AnchorTextItem {
  anchor_text: string
  link_type: string
  link_count: number
  source_pages: number
}

export function useLinkAnchors(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'links', 'anchors'],
    queryFn: () => apiClient.get<{ anchors: AnchorTextItem[]; totalAnchorCount: number; emptyAnchorCount: number }>(`/api/crawls/${crawlId}/links/anchors`),
  })
}

/** WordPress detection info from the API */
export interface WordPressInfo {
  isWordpress: boolean
  version: string | null
  seoPlugin: string | null
  issues: Array<{
    type: string
    label: string
    count: number
    severity: 'error' | 'warning' | 'info'
  }>
}

/** Get WordPress-specific info for a crawl */
export function useWordPressInfo(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'wordpress'],
    queryFn: () => apiClient.get<WordPressInfo>(`/api/crawls/${crawlId}/wordpress`),
    enabled,
  })
}

/** Sitemap record from the API */
export interface SitemapRecord {
  id: string
  url: string
  type: 'sitemap' | 'index'
  entry_count: number
  valid: number
  error_message: string | null
  fetched_at: string
}

/** Get sitemaps for a crawl */
export function useSitemaps(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'sitemaps'],
    queryFn: () => apiClient.get<{ sitemaps: SitemapRecord[] }>(`/api/crawls/${crawlId}/sitemaps`),
  })
}

/** Sitemap coverage data */
export interface SitemapCoverage {
  totalSitemapUrls: number
  totalCrawledPages: number
  inBoth: number
  inSitemapOnly: number
  inCrawlOnly: number
  coveragePercent: number
  urls: {
    inSitemapOnly: string[]
    inCrawlOnly: string[]
  }
}

/** Get sitemap coverage for a crawl */
export function useSitemapCoverage(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'sitemaps', 'coverage'],
    queryFn: () => apiClient.get<SitemapCoverage>(`/api/crawls/${crawlId}/sitemaps/coverage`),
    enabled,
  })
}

/** Resource summary data */
export interface ResourceSummary {
  totalScripts: number
  totalStylesheets: number
  totalPreloads: number
  asyncScripts: number
  deferScripts: number
  moduleScripts: number
  uniqueResourceCount: number
  pagesWithResources: number
  topResources: Array<{ src: string; type: string; pageCount: number }>
}

/** Site structure tree node */
export interface StructureNode {
  name: string
  path: string
  pageCount: number
  avgSeoScore: number
  avgResponseTime: number
  avgWordCount: number
  children: StructureNode[]
}

/** Site structure response */
export interface CrawlStructure {
  tree: StructureNode
  depthDistribution: Array<{ depth: number; count: number }>
  totalPages: number
}

/** Get site structure tree for a crawl */
export function useCrawlStructure(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'structure'],
    queryFn: () => apiClient.get<CrawlStructure>(`/api/crawls/${crawlId}/structure`),
    enabled,
  })
}

/** N-gram analysis response */
export interface NgramResult {
  n: number
  ngrams: Array<{ phrase: string; count: number }>
  totalTexts: number
}

/** Get N-gram keyword frequency for a crawl */
export function useCrawlNgrams(crawlId: string, n: number, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'content', 'ngrams', n],
    queryFn: () => apiClient.get<NgramResult>(`/api/crawls/${crawlId}/content/ngrams?n=${n}`),
    enabled,
  })
}

/** Duplicate content response */
export interface DuplicateGroup {
  contentHash: string
  pageCount: number
  pages: Array<{ url: string; title: string | null; word_count: number }>
}

export interface NearDuplicateGroup {
  groupId: number
  pageCount: number
  pages: Array<{ url: string; title: string | null; word_count: number }>
}

export interface ContentDuplicates {
  duplicateGroups: DuplicateGroup[]
  /** True distinct-content-hash group count (NOT capped by the LIMIT 50 on duplicateGroups). */
  totalDuplicateGroups: number
  totalDuplicatePages: number
  nearDuplicateGroups?: NearDuplicateGroup[]
  totalNearDuplicatePages?: number
  readability: {
    easy: number
    moderate: number
    difficult: number
    unscored: number
    avgScore: number | null
  }
}

/** Get duplicate content and readability data for a crawl */
export function useCrawlDuplicates(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'content', 'duplicates'],
    queryFn: () => apiClient.get<ContentDuplicates>(`/api/crawls/${crawlId}/content/duplicates`),
    enabled,
  })
}

/** Cannibalization group */
export interface CannibalizationGroup {
  sharedTerms: string[]
  pages: Array<{ url: string; title: string }>
}

/** Get keyword cannibalization groups for a crawl */
export function useCrawlCannibalization(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'cannibalization'],
    queryFn: () => apiClient.get<{ groups: CannibalizationGroup[]; analysedPages: number; totalTitledPages: number }>(`/api/crawls/${crawlId}/cannibalization`),
    enabled,
  })
}

/** Content cluster group */
export interface ContentCluster {
  id: number
  count: number
  pages: Array<{ url: string; title: string; wordCount: number }>
}

/** Get content clusters for a crawl */
export function useCrawlClusters(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'clusters'],
    queryFn: () => apiClient.get<{ clusters: ContentCluster[] }>(`/api/crawls/${crawlId}/clusters`),
    enabled,
  })
}

/** Custom extraction result per rule */
export interface ExtractionResult {
  label: string
  selector: string
  totalMatches: number
  pagesWithMatches: number
  pages: Array<{ url: string; values: string[] }>
}

/** Custom extraction response */
export interface CrawlExtractions {
  rules: Array<{ label: string; selector: string }>
  results: ExtractionResult[]
}

/** Get custom extraction results for a crawl */
export function useCrawlExtractions(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'extractions'],
    queryFn: () => apiClient.get<CrawlExtractions>(`/api/crawls/${crawlId}/extractions`),
    enabled,
  })
}

/** PageSpeed Insights metric */
export interface PageSpeedMetric {
  score: number | null
  value: string | null
  numericValue: number | null
}

/** PageSpeed Insights result */
export interface PageSpeedResult {
  url: string
  strategy: string
  scores: {
    performance: number | null
    accessibility: number | null
    bestPractices: number | null
    seo: number | null
  }
  coreWebVitals: {
    lcp: PageSpeedMetric
    cls: PageSpeedMetric
    fid: PageSpeedMetric
    inp: PageSpeedMetric
    fcp: PageSpeedMetric
    si: PageSpeedMetric
    tbt: PageSpeedMetric
    tti: PageSpeedMetric
  }
}

/** Security header stats */
export interface SecurityStats {
  headerStats: {
    total_200: number
    has_hsts: number
    has_csp: number
    has_xfo: number
    has_x_powered_by: number
    has_x_robots_tag: number
  }
  serverDistribution: Array<{ server: string; count: number }>
}

export function useSecurityStats(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'security'],
    queryFn: () => apiClient.get<SecurityStats>(`/api/crawls/${crawlId}/security`),
    enabled,
  })
}

/** Fetch PageSpeed Insights on demand */
export function usePageSpeed(crawlId: string, pageUrl: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'pagespeed', pageUrl],
    queryFn: () => apiClient.get<PageSpeedResult>(`/api/crawls/${crawlId}/pagespeed`, {
      params: { url: pageUrl! },
    }),
    enabled: false, // Only fetch on manual trigger
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/** CrUX field data result (p75 real-user metrics) */
export interface CruxResult {
  url: string
  scope: string
  collectionPeriod: { firstDate: { year: number; month: number; day: number }; lastDate: { year: number; month: number; day: number } } | null
  metrics: {
    lcp: number | null   // ms
    cls: number | null   // unitless (e.g. 0.12)
    inp: number | null   // ms
    fcp: number | null   // ms
    ttfb: number | null  // ms
  }
}

/** Fetch CrUX field data on demand */
export function useCruxData(crawlId: string, pageUrl: string | null, scope: 'url' | 'origin' = 'url') {
  return useQuery({
    queryKey: ['crawls', crawlId, 'crux', pageUrl, scope],
    queryFn: () => apiClient.get<CruxResult>(`/api/crawls/${crawlId}/crux`, {
      params: { url: pageUrl!, scope },
    }),
    enabled: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/** Image analysis stats */
export interface ImageStats {
  total_images: number
  missing_alt: number
  missing_dimensions: number
  no_lazy_loading: number
  pages_with_images: number
  total_pages: number
}

/** Image analysis page data */
export interface ImagePageData {
  url: string
  title: string | null
  images_data: string | null
  images_count: number
  images_missing_alt: number
  images_missing_dimensions: number
  images_no_lazy_loading: number
}

/** Get image analysis data for a crawl */
export function useImages(crawlId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'images'],
    queryFn: () => apiClient.get<{ stats: ImageStats; pages: ImagePageData[] }>(`/api/crawls/${crawlId}/images`),
    enabled,
  })
}

/** AI meta tag suggestion */
export interface AiMetaSuggestion {
  title: string
  titleLength: number
  description: string
  descriptionLength: number
}

export function useAiMetaSuggestion(crawlId: string, url: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'ai-suggest', url],
    queryFn: () => apiClient.post<AiMetaSuggestion>(`/api/crawls/${crawlId}/ai/suggest-metas`, { url }),
    enabled: false, // Manual trigger via refetch()
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  })
}

// --- Share token hooks ---

export interface ShareToken {
  id: string
  token: string
  expires_at: string | null
  created_at: string
}

export function useShareTokens(crawlId: string) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'share'],
    queryFn: () => apiClient.get<{ tokens: ShareToken[] }>(`/api/crawls/${crawlId}/share`),
  })
}

export function useCreateShareToken(crawlId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (expiryDays?: number) =>
      apiClient.post<{ id: string; token: string; url: string; expiresAt: string | null }>(
        `/api/crawls/${crawlId}/share`,
        { expiryDays }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls', crawlId, 'share'] })
    },
  })
}

export function useDeleteShareToken(crawlId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tokenId: string) =>
      apiClient.delete(`/api/crawls/${crawlId}/share/${tokenId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crawls', crawlId, 'share'] })
    },
  })
}

// ── AI Audit Summary ─────────────────────────────

export interface AiAuditSummary {
  audited: number
  avgScores?: {
    first_impression: number
    content_quality: number
    conversion_potential: number
  } | null
  topTrustSignals?: Array<{ signal: string; count: number }>
  topUxIssues?: Array<{ issue: string; count: number }>
  lowestPages?: Array<{ url: string; title: string | null; avgScore: number }>
}

export function useAiAuditSummary(crawlId: string, enabled = true) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'ai-audit-summary'],
    queryFn: () => apiClient.get<AiAuditSummary>(`/api/crawls/${crawlId}/ai-audit-summary`),
    enabled,
  })
}

// ── AI Analysis Summary (v2 — from ai_analysis JSON) ─────────────────────────────

export interface AiAnalysisSummary {
  scores: {
    total_pages: number
    avg_first_impression: number | null
    avg_content_quality: number | null
    avg_conversion_potential: number | null
    avg_seo_effectiveness: number | null
    avg_accessibility: number | null
  } | null
  pageTypes: Array<{ page_type: string; count: number }>
  topIssues: Array<{ issue_type: string; label: string; severity: string; category: string; count: number }>
  trustSignals: Array<{ signal: string; count: number }>
  lowestPages: Array<{
    url: string; title: string | null; page_type: string
    first_impression: number; content_quality: number; conversion_potential: number
    seo_effectiveness: number; accessibility: number; avg_score: number
  }>
}

export function useAiAnalysisSummary(crawlId: string, enabled = true) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'ai-analysis-summary'],
    queryFn: () => apiClient.get<AiAnalysisSummary>(`/api/crawls/${crawlId}/ai-analysis/summary`),
    enabled,
  })
}

/** AI issue pages — per-page descriptions and recommendations for an AI-detected issue type */
export interface AiIssuePage {
  url: string
  title: string | null
  description: string
  recommendation: string
  element: string | null
  severity: string
  category: string
}

export function useAiIssuePages(crawlId: string, issueType: string | null) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'ai-issues', issueType],
    queryFn: () => apiClient.get<{ pages: AiIssuePage[]; isAiIssue: boolean }>(`/api/crawls/${crawlId}/issues/${issueType}`),
    enabled: !!issueType,
  })
}

// ── Lighthouse / Core Web Vitals ─────────────────────────────

/** Lighthouse data for a crawl */
export interface LighthouseData {
  pages: Array<{
    url: string
    title: string | null
    seo_score: number | null
    inrank: number | null
    psi_performance: number | null
    psi_accessibility: number | null
    psi_best_practices: number | null
    psi_seo: number | null
    psi_lcp: number | null
    psi_cls: number | null
    psi_tbt: number | null
    psi_fcp: number | null
    psi_si: number | null
  }>
  collected: number
  totalHtmlPages: number
}

/** Get lighthouse/CWV data for a crawl */
export function useLighthouseData(crawlId: string, enabled = true) {
  return useQuery({
    queryKey: ['crawls', crawlId, 'lighthouse'],
    queryFn: () => apiClient.get<LighthouseData>(`/api/crawls/${crawlId}/lighthouse`),
    enabled,
  })
}

/** Trigger manual lighthouse collection */
export function useRunLighthouse(crawlId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (limit?: number) =>
      apiClient.post<{ ok: boolean; target: number; estimatedCost: string }>(
        `/api/crawls/${crawlId}/lighthouse`,
        { limit },
      ),
    onSuccess: () => {
      // Start polling lighthouse data
      queryClient.invalidateQueries({ queryKey: ['crawls', crawlId, 'lighthouse'] })
    },
  })
}
